import { UseFormReturn } from "react-hook-form";
import { FieldConfig } from "../lib/FormConfigs/config";
import { ProductValues, TravelBookingFormValues } from "../lib/schemas/travel-booking";
import { FormField } from "../ui-form/form-field";
import { Input } from "@/components/ui/input";
import { useMutation } from "react-query";
import { getInvoiceData } from "../apis";
import { useState } from "react";
import { CheckCircle, AlertCircle } from "lucide-react";
import { InvoiceDataResponse } from "../lib/types";
import { Img } from "components";

interface InvoiceStepProps {
    form: UseFormReturn<TravelBookingFormValues>
    config?: FieldConfig[]
    onDataExtracted?: (extractedData: InvoiceResponse, products?: ProductValues[]) => void; // Callback for when data is extracted
}

interface InvoiceResponse extends InvoiceDataResponse { }

export default function InvoiceStep({ form, onDataExtracted }: InvoiceStepProps) {
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
    const [extractedData, setExtractedData] = useState<InvoiceResponse | null>(null);
    const invoiceFile = form.watch('invoice.invoiceFile');

    const { mutate } = useMutation({
        mutationFn: getInvoiceData,
        onSuccess: (data: InvoiceResponse) => {
            setExtractedData(data);
            setUploadStatus('success');

            // Prefill form data
            prefillFormData(data);

            // Call callback if provided
            if (onDataExtracted) {
                onDataExtracted(data, createProductsFromInvoiceData(data));
            }
        },
        onError: (err: any) => {
            console.log('Error extracting invoice data:', err);
            setUploadStatus('error');
        }
    });

    const prefillFormData = (data: InvoiceResponse) => {
        try {
            // 1. Prefill Destination Data
            if (data?.product_type === 'Sale') {
                form.setValue('destination.purposeOfTravel', 'none');
                form.setValue('destination.destination', 'none');
            }
            else {
                if (data.Purpose) {
                    form.setValue('destination.purposeOfTravel', data.Purpose.toLowerCase().split('')[0].toUpperCase() + data.Purpose.toLowerCase().slice(1));
                }
                if (data.country) {
                    form.setValue('destination.destination', data.country.toLowerCase().split('')[0].toUpperCase() + data.country.toLowerCase().slice(1));
                }
            }

            // Set date range if invoice_date is available
            // if (data.invoice_date) {
            //     const [day, month, year] = data.invoice_date.split('/');
            //     const invoiceDate = new Date(`${year}-${month}-${day}`);
            //     const endDate = new Date(invoiceDate);
            //     endDate.setDate(endDate.getDate() + 30); // Add 30 days for end date

            //     form.setValue('destination.dateRange.start_date', invoiceDate.toISOString().split('T')[0]);
            // }

            // 2. Prefill Customer Data
            const customerData = {
                customerId: '', // This will be filled when customer is created/found
                fullName: data.customer_name || '',
                email: '', // Not available in invoice
                mobileNumber: '', // Not available in invoice
                pan: data.Pan || '',
                products: createProductsFromInvoiceData(data)
            };

            // 3. Prefill Delivery Address if available
            if (data.customer_address && data.customer_address.length > 0) {
                const [address1, city, pincode] = data.customer_address;

                const deliveryData = {
                    deliveryMode: { value: '', label: '' },
                    deliveryId: '',
                    products: [],
                    customers: { value: '', label: data.customer_name || '' },
                    deliveryType: '' as const,
                    locationInfo: '',
                    addresses: [{
                        address1: address1 || '',
                        address2: '',
                        landmark: '',
                        country: { value: 'in', label: 'India' },
                        state: { value: '', label: '' },
                        city: city || '',
                        pincode: pincode || ''
                    }],
                    address_id: '',
                    local_id: []
                };

                form.setValue('deliveries.0', deliveryData as any);
            }


            form.setValue('customers.0', customerData as any);
        } catch (error) {
            console.error('Error prefilling form data:', error);
        }
    };

    const createProductsFromInvoiceData = (data: InvoiceResponse) => {
        const products = [];

        // Handle multiple products
        const productCount = Math.max(
            data.currency?.length || 0,
            data.amount?.length || 0,
            data.product_id?.length || 0
        );

        const notAvailableCurrency: Record<string, string> = {
            'THAI': 'THB',
        }
        
        for (let i = 0; i < productCount; i++) {
            const currencyCode = data.currency?.[i]?.split(' ')[0] // Extract currency code
            const amount = data.amount?.[i] || 0;
            const rate = data.rate?.[i] || 0;
            const productId = data.product_id?.[i] || 16;
            const productType = (data.product_type === 'Sale' ? 'sell' : data.product_type) || 'buy';

            const product: Record<string, any> = {
                productType: {
                    value: productId.toString(),
                    label: getProductLabel(productId, productType?.toLowerCase()), // You'll need to implement this
                    type: productType?.toLowerCase()
                },
                currencies: [{
                    currency: {
                        value: currencyCode || 'THB',
                        label: currencyCode || 'THB'
                    },
                    amount: parseFloat(amount.toString()),
                    rate: parseFloat(rate.toString())
                }],
                local_id: `product_${Date.now()}_${i}` // Generate unique local_id
            };
            if (productId === 16) {
                product['cardType'] = {
                    value: 'multicurrency',
                    label: 'Multi-Currency'
                }
            }

            products.push(product);
        }

        return products;
    };


    // Helper function to get product label based on product ID
    const getProductLabel = (productId: number, typeProduct: string): string => {
        const productTypes = [
            { value: 9, label: "Forex Currency Notes", type: 'buy' },
            { value: 9, label: "Forex Currency Notes", type: 'sell' },
            { value: 36, label: "Forex Card", type: 'sell' },
            { value: 16, label: "Forex Card", type: 'buy' },
        ]
        return productTypes.find((type) => type.value === productId && type.type === typeProduct)?.label || `Product ${productId}`;
    };

    const handleFileUpload = (file: File | undefined) => {
        if (file) {
            setUploadStatus('uploading');
            form.setValue('invoice.invoiceFile', file);
            mutate(file);
        }
    };

    const renderUploadStatus = () => {
        switch (uploadStatus) {
            case 'uploading':
                return (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-blue-700 text-sm">Processing invoice...</span>
                    </div>
                );
            case 'success':
                return (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-700 text-sm">Invoice processed successfully!</span>
                        </div>
                        {extractedData && (
                            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                <h4 className="text-sm font-medium mb-2">Extracted Information:</h4>
                                <div className="text-xs space-y-1">
                                    <p><strong>Customer:</strong> {extractedData.customer_name}</p>
                                    <p><strong>Destination:</strong> {extractedData.country}</p>
                                    <p><strong>Purpose:</strong> {extractedData.Purpose}</p>
                                    <p><strong>Products:</strong> {extractedData.currency?.join(', ')}</p>
                                    <p><strong>Total Amount:</strong> ₹{extractedData.total_amount}</p>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'error':
                return (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <span className="text-red-700 text-sm">Failed to process invoice. Please try again.</span>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="space-y-4 max-w-md">
            <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                    Upload Invoice
                </label>
            </div>

            <FormField
                form={form}
                name="invoice.invoiceFile"
                render={({ field }) => (
                    <div className="space-y-3">
                        <div className="flex items-center gap-3 w-full">
                            <div
                                className={`py-1.5 px-3.5 w-full justify-between rounded-md flex border border-[#DCDEE2] ${invoiceFile?.name ? "bg-[#F2F7FF]" : "bg-[#fff]"}`}
                            >
                                <div className="flex gap-3 items-center overflow-hidden">
                                    <Img src="/images/remit/file.svg" alt="" />
                                    <p className=" text-ellipsis truncate text-[#666666] text-sm font-normal">
                                        {invoiceFile?.name || "Upload Invoice "}
                                        {" "}
                                        {
                                            invoiceFile &&
                                            <span className="text-xs text-gray-500">
                                                ({Math.round(invoiceFile.size / 1024)} KB)
                                            </span>
                                        }
                                    </p>
                                </div>
                                <label

                                    htmlFor="invoice.invoiceFile"
                                    className=" cursor-pointer text-blue-700 border border-blue-700 text-white ml-2 px-4 py-2 rounded-lg whitespace-nowrap"
                                >
                                    {invoiceFile ? "Re-upload" : "Upload"}
                                </label>
                                <Input
                                    type="file"
                                    id="invoice.invoiceFile"
                                    name="invoice.invoiceFile"
                                    className="hidden"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        field.onChange(file);
                                        handleFileUpload(file);
                                    }}
                                    value={undefined}
                                />
                            </div>
                            {/* <Label htmlFor="invoice.invoiceFile">
                                <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        field.onChange(file);
                                        handleFileUpload(file);
                                    }}
                                    value={undefined}
                                    className="flex-1 hidden"
                                    disabled={isLoading}
                                    name="invoice.invoiceFile"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        const input = document.querySelector('input[type="file"]') as HTMLInputElement;
                                        input?.click();
                                    }}
                                    disabled={isLoading}
                                >
                                    <Upload className="h-4 w-4 mr-1" />
                                    Browse
                                </Button>
                            </Label> */}
                        </div>

                        {/* {invoiceFile && (
                            <div className="flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                                <FileText className="h-4 w-4 text-gray-600" />
                                <span className="text-sm text-gray-700 truncate">
                                    {invoiceFile.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                    ({Math.round(invoiceFile.size / 1024)} KB)
                                </span>
                            </div>
                        )} */}

                        {renderUploadStatus()}
                    </div>
                )}
            />

            {uploadStatus === 'success' && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-700">
                        ✅ Form data has been automatically filled from the invoice.
                        Please review and make any necessary adjustments in the following steps.
                    </p>
                </div>
            )}
        </div>
    );
}